import {
  HHBox,
  HHButton,
  HHCard,
  HHCardContent,
  HHCircularProgress,
  HHDialog,
  HHDialogActions,
  HHDialogContent,
  HHDialogTitle,
  HHGrid,
  HHStack,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
  HHTextField,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  ArrowBack,
  CheckCircle,
  CloudOff,
  Code,
  Info,
  PlayArrow,
  Refresh,
  Rule,
  Settings,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import {
  GetRuleQuery,
  RuleStatus,
  useGetRuleQuery,
} from '../../hinge-connect/rules/types';
import { useStatusToggle } from '../hooks/use-status-toggle';
import { StatusChangeConfirmationDialog } from './status-change-confirmation-dialog';
import { StatusDropdownMenu } from './status-dropdown-menu';

type ConfigDetailsViewProps = {
  ruleId: string;
  onBack: () => void;
  onViewJson: () => void;
};

type RuleType = GetRuleQuery['getRule'];

const hasFixedWidthFormat = (rule: RuleType | undefined): boolean => {
  if (!rule?.actions) return false;

  return rule.actions.some(
    action =>
      action?.type === 'FILE_INGESTION_MESSAGE_BUILDER' &&
      action?.metadata?.parser?.fileFormat === 'fixed_width_txt',
  );
};

const getColumnsSpec = (
  rule: RuleType | undefined,
): Array<{ name: string; start_pos: number; length: number }> => {
  if (!rule?.actions) return [];

  const action = rule.actions.find(
    a =>
      a?.type === 'FILE_INGESTION_MESSAGE_BUILDER' &&
      a?.metadata?.parser?.fileFormat === 'fixed_width_txt',
  );

  return action?.metadata?.parser?.config?.columns_spec || [];
};

const getStatusColor = (status: RuleStatus): string => {
  switch (status) {
    case RuleStatus.Published:
      return '#2E7D32'; // Green
    case RuleStatus.Unpublished:
      return '#FF9800'; // Orange
    case RuleStatus.Draft:
      return '#757575'; // Gray
    default:
      return '#757575'; // Gray fallback
  }
};

const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'MMM d, yyyy • h:mm a');
  } catch {
    return 'Unknown';
  }
};

const mapResourceTypeToIngestionEvent = (resourceType: string): string => {
  const eventMap: Record<string, string> = {
    'OBJECTCREATED:PUT': 'File Created/Updated',
    'OBJECTCREATED:COPY': 'Copied',
    'OBJECTCREATED:COMPLETEMULTIPARTUPLOAD': 'File Created/Updated',
  };

  return eventMap[resourceType] || resourceType;
};

const getUniqueIngestionEventLabels = (resourceTypes: string[]): string[] => {
  const mappedLabels = resourceTypes.map(mapResourceTypeToIngestionEvent);
  return [...new Set(mappedLabels)];
};

const mapRuleOperator = (operator: string): string => {
  const operatorMap: Record<string, string> = {
    all: 'AND',
    any: 'OR',
    ALL: 'AND',
    ANY: 'OR',
  };

  return operatorMap[operator] || operator.toUpperCase();
};

const BackNavigation = ({ onBack }: { onBack: () => void }): JSX.Element => (
  <HHBox paddingX="2rem" paddingTop="1rem">
    <HHButton
      hhVariant="text"
      startIcon={<ArrowBack />}
      onClick={onBack}
      sx={{
        color: '#1565C0',
        fontWeight: 500,
        textTransform: 'none',
        padding: '6px 12px',
        borderRadius: '8px',
        '&:hover': {
          backgroundColor: '#E3F2FD',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      Back to Configurations
    </HHButton>
  </HHBox>
);

const LoadingState = ({ onBack }: { onBack: () => void }): JSX.Element => (
  <HHBox paddingX="0" bgcolor="#ffffff">
    <HHStack direction="column" spacing={8} height="100%">
      <BackNavigation onBack={onBack} />
      <HHBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingY: 8,
          paddingX: '2rem',
          textAlign: 'center',
          minHeight: '400px',
        }}
      >
        <HHCircularProgress
          size={60}
          thickness={3}
          sx={{
            color: '#1565C0',
            marginBottom: 3,
          }}
        />
        <HHTypography
          hhVariant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            marginBottom: 1,
          }}
        >
          Loading Configuration Details
        </HHTypography>
        <HHTypography
          hhVariant="body1"
          sx={{
            color: 'text.secondary',
            maxWidth: '400px',
            lineHeight: 1.6,
          }}
        >
          Fetching detailed configuration...
        </HHTypography>
      </HHBox>
    </HHStack>
  </HHBox>
);

const ErrorState = ({
  onBack,
  error,
}: {
  onBack: () => void;
  error: Error | undefined;
}): JSX.Element => (
  <HHBox paddingX="0" bgcolor="#ffffff">
    <HHStack direction="column" spacing={6} height="100%">
      <BackNavigation onBack={onBack} />
      <HHBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingY: 8,
          paddingX: '2rem',
          textAlign: 'center',
          minHeight: '500px',
        }}
      >
        <HHBox
          sx={{
            backgroundColor: '#FFEBEE',
            background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
            borderRadius: '50%',
            padding: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 3,
            boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
            border: '2px solid #FFCDD2',
          }}
        >
          <CloudOff
            sx={{
              fontSize: '64px',
              color: '#D32F2F',
            }}
          />
        </HHBox>
        <HHTypography
          hhVariant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '32px', md: '36px' },
            lineHeight: 1.2,
            color: '#D32F2F',
            marginBottom: 2,
            letterSpacing: '-0.02em',
          }}
        >
          Connection Lost
        </HHTypography>
        <HHTypography
          hhVariant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: '18px',
            lineHeight: 1.6,
            maxWidth: '500px',
            marginBottom: 4,
          }}
        >
          We couldn't load the configuration details. This might be due to a
          network issue or the configuration may no longer be available.
        </HHTypography>
        <HHBox
          sx={{
            backgroundColor: '#F5F5F5',
            border: '1px solid #E0E0E0',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: 4,
            maxWidth: '500px',
            width: '100%',
          }}
        >
          <HHStack direction="column" spacing={2}>
            <HHTypography
              hhVariant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: 'text.primary',
              }}
            >
              What you can try:
            </HHTypography>
            <HHStack direction="column" spacing={1} alignItems="flex-start">
              {[
                'Check your internet connection',
                'Refresh the page and try again',
                'Contact support if the problem persists',
              ].map(suggestion => (
                <HHStack
                  key={suggestion}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                >
                  <HHBox
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#D32F2F',
                    }}
                  />
                  <HHTypography
                    hhVariant="body2"
                    sx={{ color: 'text.secondary', fontSize: '14px' }}
                  >
                    {suggestion}
                  </HHTypography>
                </HHStack>
              ))}
            </HHStack>
          </HHStack>
        </HHBox>
        <HHStack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          justifyContent="center"
        >
          <HHButton
            hhVariant="contained"
            startIcon={<Refresh />}
            onClick={(): void => globalThis.location.reload()}
            sx={{
              backgroundColor: '#D32F2F',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#B71C1C',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Try Again
          </HHButton>
          <HHButton
            hhVariant="outlined"
            onClick={onBack}
            sx={{
              borderColor: '#D32F2F',
              color: '#D32F2F',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#FFEBEE',
                borderColor: '#B71C1C',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Go Back
          </HHButton>
        </HHStack>
        {error && (
          <HHBox
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#FFF3E0',
              border: '1px solid #FFB74D',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '100%',
            }}
          >
            <HHTypography
              hhVariant="caption"
              sx={{
                color: '#E65100',
                fontSize: '12px',
                fontFamily: 'monospace',
                wordBreak: 'break-word',
              }}
            >
              Error: {error.message || 'Unknown error occurred'}
            </HHTypography>
          </HHBox>
        )}
      </HHBox>
    </HHStack>
  </HHBox>
);

const HeaderSection = ({
  rule,
  statusColor,
  onBack,
  onViewJson,
  statusUpdateLoading,
  onStatusChange,
}: {
  rule: RuleType;
  statusColor: string;
  onBack: () => void;
  onViewJson: () => void;
  statusUpdateLoading: boolean;
  onStatusChange: (newStatus: RuleStatus) => void;
}): JSX.Element => (
  <>
    <BackNavigation onBack={onBack} />
    <HHBox
      sx={{
        position: 'relative',
        marginBottom: '16px',
        paddingX: '2rem',
      }}
    >
      <HHStack direction="row" alignItems="center" spacing={3}>
        <HHBox
          sx={{
            backgroundColor: statusColor,
            background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}CC 100%)`,
            borderRadius: '16px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 20px ${statusColor}40`,
          }}
        >
          <Rule
            sx={{
              fontSize: '32px',
              color: 'white',
            }}
          />
        </HHBox>
        <HHStack direction="column" spacing={0.5} sx={{ flex: 1 }}>
          <HHTypography
            hhVariant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '24px', sm: '28px', md: '32px' },
              lineHeight: 1.2,
              background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}CC 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            {rule.definition.name}
          </HHTypography>
          <HHStack direction="row" alignItems="center" spacing={2}>
            <StatusDropdownMenu
              currentStatus={rule.status}
              loading={statusUpdateLoading}
              onStatusChange={onStatusChange}
            />
            <HHTypography
              hhVariant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '14px',
              }}
            >
              Source: {rule.source}
            </HHTypography>
          </HHStack>
        </HHStack>
        <HHStack direction="row" alignItems="center" spacing={1}>
          <HHButton
            hhVariant="text"
            startIcon={<Code />}
            onClick={onViewJson}
            sx={{
              color: statusColor,
              fontWeight: 500,
              textTransform: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1px solid ${statusColor}30`,
              '&:hover': {
                backgroundColor: `${statusColor}08`,
                borderColor: statusColor,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            View JSON
          </HHButton>
        </HHStack>
      </HHStack>
      <HHBox
        sx={{
          position: 'absolute',
          bottom: '-12px',
          left: '34px',
          width: '120px',
          height: '4px',
          background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}CC 100%)`,
          borderRadius: '2px',
          opacity: 0.6,
        }}
      />
    </HHBox>
  </>
);

const ConfigurationOverviewCard = ({
  rule,
  statusColor,
}: {
  rule: RuleType;
  statusColor: string;
}): JSX.Element => (
  <HHCard
    sx={{
      border: '1px solid #E0E0E0',
      borderRadius: '12px',
      backgroundColor: `${statusColor}03`,
    }}
  >
    <HHCardContent sx={{ padding: '24px' }}>
      <HHStack direction="column" spacing={4}>
        <HHStack direction="row" alignItems="center" spacing={2}>
          <Settings sx={{ color: statusColor, fontSize: '20px' }} />
          <HHTypography
            hhVariant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              color: 'text.primary',
            }}
          >
            Configuration Overview
          </HHTypography>
        </HHStack>
        <HHStack direction="row" spacing={4}>
          <HHBox>
            <HHTypography
              hhVariant="h4"
              sx={{
                color: 'text.secondary',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Ingestion Events
            </HHTypography>
            <HHStack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ marginTop: 0.5 }}
            >
              {rule.resourceTypes && rule.resourceTypes.length > 0 ? (
                getUniqueIngestionEventLabels(rule.resourceTypes).map(label => (
                  <Chip
                    key={label}
                    label={label}
                    variant="outlined"
                    size="small"
                    sx={{
                      backgroundColor: '#F5F5F5',
                      color: 'text.secondary',
                      fontSize: '11px',
                      '& .MuiChip-label': {
                        fontSize: '11px',
                      },
                    }}
                  />
                ))
              ) : (
                <HHTypography hhVariant="body2" color="text.secondary">
                  All ingestion events
                </HHTypography>
              )}
            </HHStack>
          </HHBox>
          <HHBox>
            <HHTypography
              hhVariant="h4"
              sx={{
                color: 'text.secondary',
                fontSize: '10px',
                fontWeight: 400,
                textTransform: 'uppercase',
              }}
            >
              Created
            </HHTypography>
            <HHTypography
              hhVariant="body2"
              sx={{
                color: 'text.primary',
                fontSize: '14px',
              }}
            >
              {formatDate(rule.createdAt)}
            </HHTypography>
          </HHBox>
          <HHBox>
            <HHTypography
              hhVariant="h4"
              sx={{
                color: 'text.secondary',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Last Updated
            </HHTypography>
            <HHTypography
              hhVariant="caption"
              sx={{
                color: 'text.primary',
                fontSize: '14px',
              }}
            >
              {formatDate(rule.updatedAt)}
            </HHTypography>
          </HHBox>
          <HHBox>
            <HHTypography
              hhVariant="h4"
              sx={{
                color: 'text.secondary',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Logic Operator
            </HHTypography>
            <HHTypography
              hhVariant="body2"
              textTransform="uppercase"
              sx={{
                color: 'text.primary',
                fontSize: '14px',
              }}
            >
              {rule.definition.operator || 'Not specified'}
            </HHTypography>
          </HHBox>
        </HHStack>
        <HHBox>
          <HHStack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ marginBottom: 2 }}
          >
            <CheckCircle sx={{ color: '#2E7D32', fontSize: '18px' }} />
            <HHTypography
              hhVariant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: 'text.primary',
              }}
            >
              File Criteria
            </HHTypography>
            <HHTooltip
              hhVariant="variant-bypass"
              title={`A file will be processed when ${mapRuleOperator(rule.definition.operator || 'all')} of the below criteria are met`}
              placement="top"
            >
              <Info
                sx={{
                  fontSize: '16px',
                  color: '#666',
                  cursor: 'help',
                }}
              />
            </HHTooltip>
          </HHStack>
          <HHStack direction="column" spacing={2}>
            {rule.definition.criteria.map((criterion, index) => {
              const criterionTyped = criterion as {
                key: string;
                name: string;
                value: string;
                operator: string;
              };

              const isLastCriterion =
                index === rule.definition.criteria.length - 1;

              return (
                <HHBox
                  key={`criterion-${criterionTyped.key}-${criterionTyped.value}`}
                >
                  <HHBox
                    sx={{
                      backgroundColor: 'white',
                      border: '1px solid #E0E0E0',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      borderLeft: '3px solid #1565C0',
                    }}
                  >
                    <HHTypography
                      hhVariant="variant-bypass"
                      sx={{
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      When{' '}
                      <HHTypography
                        component="span"
                        hhVariant="variant-bypass"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: '#E8F4FD',
                          color: '#1565C0',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 600,
                        }}
                      >
                        {criterionTyped.key}
                      </HHTypography>{' '}
                      <HHTypography
                        component="span"
                        hhVariant="variant-bypass"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: '#F0F0F0',
                          color: '#616161',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 600,
                        }}
                      >
                        {criterionTyped.operator}
                      </HHTypography>{' '}
                      <HHTypography
                        component="span"
                        hhVariant="variant-bypass"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: '#F8F9FA',
                          color: '#424242',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 600,
                        }}
                      >
                        {criterionTyped.value}
                      </HHTypography>
                    </HHTypography>
                  </HHBox>
                  {!isLastCriterion && (
                    <HHBox
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingY: 1,
                      }}
                    >
                      <HHTypography
                        hhVariant="variant-bypass"
                        sx={{
                          fontWeight: 700,
                          fontSize: '12px',
                          color: '#1565C0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {mapRuleOperator(rule.definition.operator || 'all')}
                      </HHTypography>
                    </HHBox>
                  )}
                </HHBox>
              );
            })}
          </HHStack>
        </HHBox>
      </HHStack>
    </HHCardContent>
  </HHCard>
);

const FixedWidthTestCard = ({
  onOpenModal,
}: {
  onOpenModal: () => void;
}): JSX.Element => (
  <HHCard
    sx={{
      border: '1px solid #E0E0E0',
      borderRadius: '12px',
      backgroundColor: '#F8F9FA',
    }}
  >
    <HHCardContent sx={{ padding: '24px' }}>
      <HHStack direction="column" spacing={2}>
        <HHTypography
          hhVariant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            color: 'text.primary',
          }}
        >
          Fixed Width Configuration Testing
        </HHTypography>
        <HHTypography
          hhVariant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '14px',
          }}
        >
          Test your fixed width configuration to ensure it parses files
          correctly.
        </HHTypography>
        <HHButton
          hhVariant="contained"
          onClick={onOpenModal}
          sx={{
            backgroundColor: '#1565C0',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#0D47A1',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Test fixed width config
        </HHButton>
      </HHStack>
    </HHCardContent>
  </HHCard>
);

type ColumnSpec = {
  name: string;
  start_pos: number;
  length: number;
};

type ParsedRow = {
  [columnName: string]: string; // Dynamic keys based on column names
};

const FixedWidthTestModal = ({
  open,
  onClose,
  columnsSpec,
}: {
  open: boolean;
  onClose: () => void;
  columnsSpec: ColumnSpec[];
}): JSX.Element => {
  const [inputText, setInputText] = useState('');
  const [parsedResults, setParsedResults] = useState<ParsedRow[]>([]);

  const handleTest = (): void => {
    if (!inputText.trim()) {
      setParsedResults([]);
      return;
    }

    const rows = inputText.split('\n').filter(row => row.trim());

    const results = rows.map(row => {
      const parsedRow: ParsedRow = {};

      columnsSpec.forEach(col => {
        // Config uses 1-based indexing, JavaScript substring uses 0-based
        const startIndex = col.start_pos - 1;
        const value = row.substring(startIndex, startIndex + col.length).trim();
        parsedRow[col.name] = value || '(empty)';
      });

      return parsedRow;
    });

    setParsedResults(results);
  };

  const handleClose = (): void => {
    setInputText('');
    setParsedResults([]);
    onClose();
  };

  return (
    <HHDialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <HHDialogTitle>Test Fixed Width Configuration</HHDialogTitle>
      <HHDialogContent>
        <HHStack direction="column" spacing={3} sx={{ paddingTop: 2 }}>
          <HHBox>
            <HHTypography
              hhVariant="body2"
              sx={{
                marginBottom: 1,
                fontWeight: 500,
                color: 'text.primary',
              }}
            >
              Sample Data
            </HHTypography>
            <HHTextField
              hhVariant="outlined"
              fullWidth
              multiline
              rows={6}
              value={inputText}
              onChange={(e): void => setInputText(e.target.value)}
              placeholder="Enter fixed-width file rows to test...&#10;Example:&#10;John      Doe  30&#10;Jane      Smith25"
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '14px',
                },
              }}
            />
          </HHBox>

          <HHBox>
            <HHTypography
              hhVariant="body2"
              sx={{
                marginBottom: 1,
                fontWeight: 500,
                color: 'text.secondary',
              }}
            >
              Configuration: {columnsSpec.length} column(s) defined
            </HHTypography>
          </HHBox>

          {parsedResults.length > 0 && (
            <HHBox>
              <HHTypography
                hhVariant="h6"
                sx={{
                  marginBottom: 2,
                  fontWeight: 600,
                  fontSize: '16px',
                  color: 'text.primary',
                }}
              >
                Parsed Results
              </HHTypography>
              <HHTableContainer
                sx={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  maxHeight: '400px',
                }}
              >
                <HHTable stickyHeader>
                  <HHTableHead>
                    <HHTableRow>
                      {columnsSpec.map(col => (
                        <HHTableCell
                          key={col.name}
                          sx={{
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          {col.name}
                        </HHTableCell>
                      ))}
                    </HHTableRow>
                  </HHTableHead>
                  <HHTableBody>
                    {parsedResults.map((row, rowIndex) => (
                      <HHTableRow
                        key={`row-${rowIndex}`}
                        sx={{
                          '&:nth-of-type(even)': {
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                      >
                        {columnsSpec.map(col => (
                          <HHTableCell
                            key={`${rowIndex}-${col.name}`}
                            sx={{
                              fontSize: '13px',
                              fontFamily: 'monospace',
                              backgroundColor: '#F8F9FA',
                              color:
                                row[col.name] === '(empty)'
                                  ? 'text.disabled'
                                  : 'text.primary',
                            }}
                          >
                            {row[col.name]}
                          </HHTableCell>
                        ))}
                      </HHTableRow>
                    ))}
                  </HHTableBody>
                </HHTable>
              </HHTableContainer>
            </HHBox>
          )}
        </HHStack>
      </HHDialogContent>
      <HHDialogActions>
        <HHButton
          hhVariant="text"
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
          }}
        >
          Close
        </HHButton>
        <HHButton
          hhVariant="contained"
          onClick={handleTest}
          startIcon={<PlayArrow />}
          disabled={!inputText.trim() || columnsSpec.length === 0}
          sx={{
            backgroundColor: '#1565C0',
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#0D47A1',
            },
          }}
        >
          Test Configuration
        </HHButton>
      </HHDialogActions>
    </HHDialog>
  );
};

const ConfigurationUsageLogCard = ({
  statusColor,
}: {
  statusColor: string;
}): JSX.Element => (
  <HHCard
    sx={{
      border: '1px solid #E0E0E0',
      borderRadius: '12px',
      backgroundColor: '#F8F9FA',
      height: '100%',
    }}
  >
    <HHCardContent sx={{ padding: '24px' }}>
      <HHStack direction="column" spacing={3}>
        <HHStack direction="row" alignItems="center" spacing={2}>
          <Settings sx={{ color: statusColor, fontSize: '20px' }} />
          <HHTypography
            hhVariant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              color: 'text.primary',
            }}
          >
            Configuration Usage Log
          </HHTypography>
        </HHStack>
        <HHBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center',
            minHeight: '300px',
          }}
        >
          <HHBox
            sx={{
              fontSize: '48px',
              marginBottom: 2,
              opacity: 0.7,
            }}
          >
            🚧
          </HHBox>
          <HHTypography
            hhVariant="h4"
            sx={{
              fontWeight: 700,
              fontSize: '24px',
              color: 'text.primary',
              marginBottom: 1,
            }}
          >
            Coming Soon™
          </HHTypography>
          <HHTypography
            hhVariant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '16px',
              lineHeight: 1.6,
              maxWidth: '300px',
              marginBottom: 2,
            }}
          >
            We're cooking up something amazing! Configuration usage logs will
            show you exactly when, how, and why your configurations are used.
          </HHTypography>
          <HHBox
            sx={{
              display: 'flex',
              gap: 1,
              fontSize: '14px',
              opacity: 0.6,
            }}
          >
            ⏱️ 📊 🎯
          </HHBox>
          <HHTypography
            hhVariant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '12px',
              fontStyle: 'italic',
              marginTop: 2,
              opacity: 0.7,
            }}
          >
            (Stay tuned for execution insights!)
          </HHTypography>
        </HHBox>
      </HHStack>
    </HHCardContent>
  </HHCard>
);

export const ConfigDetailsView = ({
  ruleId,
  onBack,
  onViewJson,
}: ConfigDetailsViewProps): JSX.Element => {
  const {
    data: ruleData,
    loading,
    error,
  } = useGetRuleQuery({
    variables: { id: ruleId },
  });

  // Status toggle functionality
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    ruleId: string;
    ruleName: string;
    currentStatus: RuleStatus;
    targetStatus: RuleStatus;
  }>({
    open: false,
    ruleId: '',
    ruleName: '',
    currentStatus: RuleStatus.Draft,
    targetStatus: RuleStatus.Published,
  });

  const { loading: statusUpdateLoading, updateRuleStatus } = useStatusToggle();

  // Fixed width config test modal
  const [isFixedWidthTestModalOpen, setIsFixedWidthTestModalOpen] =
    useState(false);

  // Status change handlers
  const handleStatusChange = (
    ruleIdParam: string,
    ruleName: string,
    currentStatus: RuleStatus,
    targetStatus: RuleStatus,
  ): void => {
    setConfirmationDialog({
      open: true,
      ruleId: ruleIdParam,
      ruleName,
      currentStatus,
      targetStatus,
    });
  };

  const handleConfirmStatusChange = async (): Promise<void> => {
    try {
      await updateRuleStatus(
        confirmationDialog.ruleId,
        confirmationDialog.targetStatus,
      );
      setConfirmationDialog(prev => ({ ...prev, open: false }));
    } catch (err) {
      console.error('Failed to update rule status:', err);
    }
  };

  const handleCancelStatusChange = (): void => {
    setConfirmationDialog(prev => ({ ...prev, open: false }));
  };

  const rule = ruleData?.getRule;

  if (loading) {
    return <LoadingState onBack={onBack} />;
  }

  if (error || !rule) {
    return <ErrorState onBack={onBack} error={error} />;
  }

  const statusColor = getStatusColor(rule.status);

  return (
    <HHBox paddingX="0" bgcolor="#ffffff">
      <HHStack direction="column" spacing={6} height="100%">
        <HeaderSection
          rule={rule}
          statusColor={statusColor}
          onBack={onBack}
          onViewJson={onViewJson}
          statusUpdateLoading={statusUpdateLoading}
          onStatusChange={(newStatus): void =>
            handleStatusChange(
              ruleId,
              rule.definition.name,
              rule.status,
              newStatus,
            )
          }
        />
        <HHBox paddingX="2rem">
          <HHGrid container spacing={4}>
            <HHGrid item xs={12} md={6}>
              <HHStack direction="column" spacing={4}>
                <ConfigurationOverviewCard
                  rule={rule}
                  statusColor={statusColor}
                />
                {hasFixedWidthFormat(rule) && (
                  <FixedWidthTestCard
                    onOpenModal={(): void => setIsFixedWidthTestModalOpen(true)}
                  />
                )}
              </HHStack>
            </HHGrid>
            <HHGrid item xs={12} md={6}>
              <ConfigurationUsageLogCard statusColor={statusColor} />
            </HHGrid>
          </HHGrid>
        </HHBox>
      </HHStack>

      <StatusChangeConfirmationDialog
        open={confirmationDialog.open}
        currentStatus={confirmationDialog.currentStatus}
        targetStatus={confirmationDialog.targetStatus}
        ruleName={confirmationDialog.ruleName}
        loading={statusUpdateLoading}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
      />

      <FixedWidthTestModal
        open={isFixedWidthTestModalOpen}
        onClose={(): void => setIsFixedWidthTestModalOpen(false)}
        columnsSpec={getColumnsSpec(rule)}
      />
    </HHBox>
  );
};
